import { SCRIM_GRADIENT_HEAVY } from '@/components/page-builder/blocks/scrimGradient';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { AuthorByline } from './AuthorByline';
import type { SectionTheme } from './constants';
import { articleUrl, itemVariants, sectionColors, sectionVariants } from './constants';
import type { JournalGuide, JournalSection } from './types';

function LeadCard({ guide, reversed = false, colors }: { guide: JournalGuide; reversed?: boolean; colors: ReturnType<typeof sectionColors> }) {
    const tag = guide.kicker ?? guide.tags?.[0]?.name ?? guide.country_region;

    return (
        <Link
            href={articleUrl(guide.slug)}
            className={cn('group grid gap-5 sm:gap-6 lg:gap-10', reversed ? 'sm:grid-cols-[3fr_2fr]' : 'sm:grid-cols-[3fr_2fr]')}
        >
            {guide.main_image && (
                <div className={cn('relative aspect-[16/10] overflow-hidden', reversed && 'sm:order-last')}>
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="flex flex-col justify-center">
                {tag && <span className={cn('lux-caps lux-caps-sm mb-2', colors.kicker)}>{tag}</span>}
                <h3 className={cn('lux-didot lux-didot-xl transition-colors', colors.cardTitle, colors.cardHover)}>{guide.title}</h3>
                {guide.excerpt && <p className={cn('lux-body lux-body-lg mt-4 line-clamp-3', colors.body)}>{guide.excerpt}</p>}
                {guide.author_name && <AuthorByline name={guide.author_name} className={cn('mt-4', colors.byline)} />}
            </div>
        </Link>
    );
}

function MagazineLeadCard({ guide }: { guide: JournalGuide }) {
    const tag = guide.kicker ?? guide.tags?.[0]?.name ?? guide.country_region;

    return (
        <Link href={articleUrl(guide.slug)} className="group relative block aspect-[4/3] overflow-hidden sm:aspect-[16/10] lg:aspect-[2.4/1]">
            {guide.main_image && (
                <img
                    src={guide.main_image}
                    alt={guide.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    loading="lazy"
                />
            )}
            <div className="absolute inset-0" style={{ background: SCRIM_GRADIENT_HEAVY }} />
            <div className="lux-space-px absolute inset-x-0 bottom-0 pb-6 lg:pb-10">
                <div className="mx-auto max-w-[1400px]">
                    {tag && <span className="lux-caps lux-caps-sm mb-2 block text-white/60">{tag}</span>}
                    <h3 className="lux-didot text-white" style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)', lineHeight: 1.08 }}>
                        {guide.title}
                    </h3>
                    {guide.excerpt && <p className="lux-body lux-body-lg mt-3 line-clamp-2 max-w-2xl text-white/80">{guide.excerpt}</p>}
                    {guide.author_name && <AuthorByline name={guide.author_name} className="mt-4 text-white/75" />}
                </div>
            </div>
        </Link>
    );
}

function GridCard({ guide, colors, featured = false }: { guide: JournalGuide; colors: ReturnType<typeof sectionColors>; featured?: boolean }) {
    const tag = guide.kicker ?? guide.country_region ?? guide.tags?.[0]?.name;

    return (
        <Link href={articleUrl(guide.slug)} className="group">
            {guide.main_image && (
                <div className={cn('relative overflow-hidden', featured ? 'aspect-[4/3]' : 'aspect-[3/2]')}>
                    <img
                        src={guide.main_image}
                        alt={guide.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                    />
                </div>
            )}
            <div className="pt-4">
                {tag && <span className={cn('lux-caps lux-caps-sm', colors.kicker)}>{tag}</span>}
                <h4
                    className={cn('lux-didot mt-1 transition-colors', featured ? 'lux-didot-lg' : 'lux-didot-md', colors.cardTitle, colors.cardHover)}
                >
                    {guide.title}
                </h4>
                {guide.excerpt && (
                    <p className={cn('lux-body mt-2 line-clamp-2', featured ? 'lux-body-md' : 'lux-body-sm', colors.body)}>{guide.excerpt}</p>
                )}
                {guide.author_name && <AuthorByline name={guide.author_name} className={cn('mt-3', colors.byline)} />}
            </div>
        </Link>
    );
}

interface SectionStripProps {
    section: JournalSection;
    theme?: SectionTheme;
    variant?: 'default' | 'reversed' | 'magazine';
    showLead?: boolean;
}

export default function SectionStrip({ section, theme = 'teal', variant = 'default', showLead = true }: SectionStripProps) {
    const shouldReduceMotion = useReducedMotion();
    const colors = sectionColors(theme);
    const metaKicker = section.kicker;

    if (section.guides.length === 0) return null;

    const [lead, ...rest] = section.guides;
    const gridCards = showLead ? rest.slice(0, 3) : section.guides.slice(0, 3);
    const hasMore = section.totalCount > (showLead ? 4 : 3);

    const headerContent = (
        <motion.div className="mb-14" variants={shouldReduceMotion ? undefined : itemVariants}>
            {metaKicker && <span className={cn('lux-caps lux-caps-sm mb-2 block', colors.kicker)}>{metaKicker}</span>}
            <div className="flex items-baseline justify-between gap-6">
                <h2 className={cn('lux-didot', colors.heading)} style={{ fontSize: 'clamp(2.75rem, 6.5vw, 5rem)', lineHeight: 1.05 }}>
                    {section.name}
                </h2>
                {hasMore && (
                    <Link
                        href={`/journal/category/${section.tag}`}
                        className={cn('lux-caps lux-caps-sm inline-flex shrink-0 items-center gap-1.5 transition-colors', colors.link)}
                    >
                        See all
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                )}
            </div>
            <div className="mt-5 flex items-center gap-0">
                <div className={cn('h-0.5 w-12', colors.decorativeRule)} />
                <div className={cn('h-px flex-1', colors.decorativeRule, 'opacity-50')} />
            </div>
        </motion.div>
    );

    const gridContent =
        gridCards.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr] lg:gap-8">
                {gridCards.map((guide, i) => (
                    <motion.div key={guide.id} variants={shouldReduceMotion ? undefined : itemVariants}>
                        <GridCard guide={guide} colors={colors} featured={i === 0} />
                    </motion.div>
                ))}
            </div>
        ) : null;

    /* Magazine variant — full-bleed cinematic lead image */
    if (variant === 'magazine') {
        return (
            <motion.section
                id={section.tag}
                className={cn('scroll-mt-28 md:scroll-mt-32', colors.bg)}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={shouldReduceMotion ? undefined : sectionVariants}
            >
                <div className="lux-space-px">
                    <div className="mx-auto max-w-[1400px] pt-24 lg:pt-32">{headerContent}</div>
                </div>

                <motion.div className="mb-12 lg:mb-16" variants={shouldReduceMotion ? undefined : itemVariants}>
                    <MagazineLeadCard guide={lead} />
                </motion.div>

                {gridContent && (
                    <div className="lux-space-px">
                        <div className="mx-auto max-w-[1400px] pb-16 lg:pb-24">{gridContent}</div>
                    </div>
                )}
            </motion.section>
        );
    }

    /* Default & reversed variants — contained layout */
    return (
        <motion.section
            id={section.tag}
            className={cn('lux-space-px scroll-mt-28 md:scroll-mt-32', colors.bg)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={shouldReduceMotion ? undefined : sectionVariants}
        >
            <div className="mx-auto max-w-[1400px] pt-24 pb-16 lg:pt-32 lg:pb-24">
                {headerContent}

                {showLead && (
                    <motion.div className="mb-12" variants={shouldReduceMotion ? undefined : itemVariants}>
                        <LeadCard guide={lead} reversed={variant === 'reversed'} colors={colors} />
                    </motion.div>
                )}

                {gridContent}
            </div>
        </motion.section>
    );
}
